
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function AppDetailPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const appData = location.state;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const appTitle = appData?.title || 'Yapay Zeka Ranker';
    document.title = `${appTitle} | Hotel Yapay Zeka Çözümleri - OTA Sıralama ve Görünürlük Optimizasyonu`;
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', `${appTitle} - Otelinizin Booking, TripAdvisor ve Expedia gibi platformlardaki görünürlüğünü analiz eder. Yapay zeka destekli stratejik öneriler ile rakipleriniz arasından sıyrılın.`);
    }
  }, [appData]);

  return (
    <div className="bg-white text-gray-900 font-sans antialiased overflow-x-hidden">
      {/* Header */}
      <header className={`fixed top-0 z-50 w-full transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white/95 backdrop-blur-md border-b border-gray-100'}`}>
        <nav className="px-6 md:px-12 py-4 max-w-7xl mx-auto flex items-center justify-between" aria-label="Ana navigasyon">
          <div className="flex items-center gap-3">
            <Link to="/" aria-label="Hotel Yapay Zeka Çözümleri Ana Sayfa">
              <img src="https://public.readdy.ai/ai/img_res/f353b4d7-dacd-40f7-a8e4-5449d615ae6c.png" alt="Hotel Yapay Zeka Çözümleri Logo" className="h-12 w-auto cursor-pointer" />
            </Link>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium transition-colors whitespace-nowrap text-gray-900 hover:text-teal-600">Ana Sayfa</Link>
            <Link to="/#services" className="text-sm font-medium transition-colors whitespace-nowrap text-gray-900 hover:text-teal-600">Hizmetler</Link>
            <Link to="/apps" className="text-sm font-medium transition-colors whitespace-nowrap text-gray-900 hover:text-teal-600">Uygulamalar</Link>
            <Link to="/#solutions" className="text-sm font-medium transition-colors whitespace-nowrap text-gray-900 hover:text-teal-600">Çözümler</Link>
            <Link to="/#contact" className="text-sm font-medium transition-colors whitespace-nowrap text-gray-900 hover:text-teal-600">İletişim</Link>
          </div>

          <div className="flex gap-3">
            <Link to="/#contact" className="hidden sm:flex h-10 px-6 items-center justify-center rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold transition-all shadow-lg shadow-teal-600/30 whitespace-nowrap">
              <span>Ücretsiz Demo</span>
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-gray-900 cursor-pointer" aria-label="Menüyü aç/kapat" aria-expanded={mobileMenuOpen}>
              <i className="ri-menu-line text-2xl text-gray-900"></i>
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200/50 shadow-lg">
            <nav className="flex flex-col p-6 gap-4" aria-label="Mobil navigasyon">
              <Link to="/" className="text-left text-gray-900 hover:text-teal-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Ana Sayfa</Link>
              <Link to="/#services" className="text-left text-gray-900 hover:text-teal-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Hizmetler</Link>
              <Link to="/apps" className="text-left text-gray-900 hover:text-teal-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Uygulamalar</Link>
              <Link to="/#solutions" className="text-left text-gray-900 hover:text-teal-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Çözümler</Link>
              <Link to="/#contact" className="text-left text-gray-900 hover:text-teal-600 font-medium" onClick={() => setMobileMenuOpen(false)}>İletişim</Link>
            </nav>
          </div>
        )}
      </header>

      <main>
        {/* Hero Section */}
        <section className="py-24 pt-32 bg-gray-50 relative overflow-hidden" aria-labelledby="app-detail-title">
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <article className="flex-1 space-y-8">
                <h1 id="app-detail-title" className="text-3xl md:text-4xl font-black text-gray-900">
                  <strong className="text-teal-600 border-b-4 border-teal-300">{appData?.title || 'Yapay Zeka Ranker'}</strong>
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed">
                  <strong>Yapay Zeka Ranker</strong>, otelinizin Booking, TripAdvisor ve Expedia gibi platformlardaki görünürlüğünü analiz eder. Yapay zeka destekli algoritmalarımız, rakipleriniz arasından sıyrılmanız için size özel <strong>stratejik öneriler</strong> sunar.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-teal-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-900 font-medium">OTA Sıralama Takibi</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-teal-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-900 font-medium">Rakip Analizi</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-teal-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-900 font-medium">Görünürlük Optimizasyonu</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-teal-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-900 font-medium">Performans Raporları</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-teal-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-900 font-medium">Yapay Zeka Önerileri</span>
                  </li>
                </ul>
                <Link to="/#contact" className="flex items-center font-bold text-teal-600 hover:text-teal-700 gap-2 group cursor-pointer">
                  Uygulamayı Keşfet
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
              </article>

              <figure className="flex-1 w-full relative">
                <div className="rounded-xl bg-white shadow-2xl border border-gray-200 overflow-hidden transform lg:rotate-1 hover:rotate-0 transition-all duration-500">
                  <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                  </div>
                  <div>
                    <img src="https://hotelai.io/storage/hotelai-ranker.png" alt="Yapay Zeka Ranker Dashboard - OTA sıralama ve görünürlük analizi ekranı" title="Yapay Zeka Ranker Dashboard" className="w-full h-auto" loading="lazy" />
                  </div>
                </div>
                <figcaption className="sr-only">Yapay Zeka Ranker uygulama arayüzü</figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 border-t border-gray-100" aria-labelledby="detail-cta-title">
          <div className="container mx-auto px-6 max-w-4xl text-center">
            <h2 id="detail-cta-title" className="text-3xl font-bold text-gray-900 mb-6">Hemen Dijital Dönüşümü Başlatın</h2>
            <p className="text-gray-600 text-lg mb-10">
              Otelinizin ihtiyaçlarına en uygun <strong>yapay zeka çözümünü</strong> birlikte planlayalım. Daha fazla bilgi için ekibimizle iletişime geçin.
            </p>
            <Link to="/#contact" className="inline-flex items-center gap-3 px-10 py-5 bg-gray-900 text-white rounded-2xl font-black text-lg hover:scale-105 transition-transform shadow-xl cursor-pointer">
              <i className="ri-message-3-line text-2xl" aria-hidden="true"></i>
              Bilgi Almak İçin Yazın
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-teal-900 text-white pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
            <div className="col-span-1">
              <div className="flex items-center gap-2 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/f353b4d7-dacd-40f7-a8e4-5449d615ae6c.png" alt="Hotel Yapay Zeka Çözümleri Logo" className="h-10 w-auto" />
              </div>
              <p className="text-teal-200 text-sm leading-relaxed mb-6">
                Oteller için geliştirilmiş yeni nesil <strong>yapay zeka asistanı</strong>. Misafir memnuniyetini artırın, maliyetleri düşürün.
              </p>
              <div className="flex gap-4">
                <img src="https://hotelai.io/storage/google-play.png" alt="Google Play'den İndir" title="Google Play" className="h-10 w-auto" />
                <img src="https://hotelai.io/storage/apple-store.png" alt="App Store'dan İndir" title="App Store" className="h-10 w-auto" />
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-6 text-lg"><a href="#" className="hover:text-teal-300 transition-colors">Şirket</a></h4>
              <ul className="space-y-3 text-sm text-teal-200">
                <li><a href="https://yapayzekacozumleri.com/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Hakkımızda</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Gizlilik Politikası</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Hizmet Şartları</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">KVKK</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-6 text-lg"><a href="/#contact" className="hover:text-teal-300 transition-colors">İletişim</a></h4>
              <address className="not-italic">
                <ul className="space-y-3 text-sm text-teal-200">
                  <li className="flex items-center gap-2">
                    <i className="ri-mail-line text-teal-400" aria-hidden="true"></i>
                    <a href="mailto:yapayzekacozumlerim@gmail.com" className="hover:text-white transition-colors cursor-pointer">
                      yapayzekacozumlerim@gmail.com
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-global-line text-teal-400" aria-hidden="true"></i>
                    <a href="https://yapayzekacozumleri.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                      yapayzekacozumleri.com
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-global-line text-teal-400" aria-hidden="true"></i>
                    <a href="https://hotelyapayzekacozumleri.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                      hotelyapayzekacozumleri.com
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-phone-line text-teal-400" aria-hidden="true"></i>
                    <a href="tel:+905542367386" className="hover:text-white transition-colors cursor-pointer">
                      0 554 236 73 86
                    </a>
                  </li>
                </ul>
              </address>
            </div>
          </div>

          <div className="border-t border-teal-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-teal-300 text-sm">
              © 2025 Hotel Yapay Zeka Çözümleri. Tüm hakları saklıdır.
            </p>
            <div className="flex gap-6 text-sm text-teal-300">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="nofollow noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
